import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { SectionCard } from "./SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { formatSpendPercent } from "../utils";
import { useTopProviders, type StatsFilters } from "../api";

type Props = { filters: StatsFilters };
type ProviderUsageRow = {
  provider: string;
  spend: number;
  successful: number;
  failed: number;
  tokens: number;
};
type ProviderTooltipPayload = {
  payload?: ProviderUsageRow;
  value?: number;
};
type ProviderTooltipProps = {
  active?: boolean;
  payload?: ProviderTooltipPayload[];
};

const COLORS = ["#86c9a6", "#b7e2cb", "#d5efe0", "#e9f6ee"];

function ProviderUsageTooltip({
  active,
  payload = [],
}: ProviderTooltipProps) {
  const row = payload[0]?.payload;
  if (!active || !row) return null;

  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-xl">
      <p className="font-medium text-popover-foreground">{row.provider}</p>
      <p className="mt-1 text-muted-foreground">
        {formatSpendPercent(row.spend)}
      </p>
    </div>
  );
}

export function ProviderUsage({ filters }: Props) {
  const { t } = useTranslation();
  const { data = [], isLoading } = useTopProviders(filters, 10);

  const rows = data.map((row) => ({
    provider: row.provider ?? "unknown",
    spend: row.total_spend,
    successful: row.successful_requests,
    failed: row.failed_requests,
    tokens: row.total_tokens,
  }));
  const totalSpend = rows.reduce((sum, r) => sum + r.spend, 0);

  return (
    <SectionCard
      title={t("usageAndCost.providerUsage.title")}
      category={t("usageAndCost.categories.all")}
      bodyClassName="p-0"
    >
      <div className="flex flex-col items-center gap-6 p-5 md:flex-row md:items-center">
        <div className="relative size-[220px] shrink-0" dir="ltr">
          {isLoading ? (
            <Skeleton className="size-full rounded-full" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rows}
                    dataKey="spend"
                    nameKey="provider"
                    innerRadius={65}
                    outerRadius={95}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {rows.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<ProviderUsageTooltip />}
                    cursor={false}
                    wrapperStyle={{
                      outline: "none",
                      pointerEvents: "none",
                      zIndex: 10,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-semibold text-foreground">
                  {formatSpendPercent(totalSpend)}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex-1 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="px-3 py-2 text-start text-xs font-medium">
                  {t("usageAndCost.providerUsage.provider")}
                </th>
                <th className="px-3 py-2 text-end text-xs font-medium">
                  {t("usageAndCost.providerUsage.spendPercent")}
                </th>
                <th className="px-3 py-2 text-end text-xs font-medium">
                  {t("usageAndCost.providerUsage.successful")}
                </th>
                <th className="px-3 py-2 text-end text-xs font-medium">
                  {t("usageAndCost.providerUsage.failed")}
                </th>
                <th className="px-3 py-2 text-end text-xs font-medium">
                  {t("usageAndCost.providerUsage.tokens")}
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr
                    key={i}
                    className="border-b border-border last:border-b-0"
                  >
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="px-3 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-sm text-muted-foreground"
                  >
                    {t("usageAndCost.providerUsage.noData")}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr
                    key={`${row.provider}-${idx}`}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-2 text-foreground">
                        <span
                          className="inline-flex size-5 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                          style={{
                            backgroundColor: COLORS[idx % COLORS.length],
                          }}
                        >
                          {row.provider.charAt(0).toUpperCase()}
                        </span>
                        {row.provider}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-end text-foreground">
                      {formatSpendPercent(row.spend)}
                    </td>
                    <td className="px-3 py-3 text-end text-emerald-400">
                      {row.successful}
                    </td>
                    <td className="px-3 py-3 text-end text-destructive">
                      {row.failed}
                    </td>
                    <td className="px-3 py-3 text-end text-muted-foreground">
                      {row.tokens.toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
}
